import { ControlValueAccessor } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import {
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  OnDestroy,
  Output,
} from '@angular/core';
import { ToasterService } from '@digistore/ds24-ui/toaster';
import { DOCUMENT } from '@angular/common';

@Component({
  template: '',
})
export abstract class AbstractMethodComponent
  implements ControlValueAccessor, OnDestroy
{
  static externalResourcesLoaded = new Set<string>();
  protected _paymentDataChanged = new Subject<any>();
  protected _destroy$ = new Subject<void>();
  protected _subscriptionRegisterOnChange: Subscription;
  protected _debugLabel = '';

  @Output() readonly notAvailable = new EventEmitter<void>();

  protected _toaster = this._injector.get(ToasterService);
  protected _element: ElementRef<HTMLElement> = this._injector.get(ElementRef);

  static uniqueInstanceIdGenerator = 0;
  uniqueInstanceId = AbstractMethodComponent.uniqueInstanceIdGenerator++;

  protected _document = this._injector.get(DOCUMENT);

  constructor(protected _injector: Injector) {}

  protected isActive(): boolean {
    const el = this._element.nativeElement;
    /**
     * Attenation: a page builder update could break this behavior!
     */
    const isDesktopOnlyVisible = el.closest('.pgb-desktop-only');
    const isMobileOnlyVisible = el.closest('.pgb-mobile-only');
    return (
      (isDesktopOnlyVisible !== null &&
        el.closest('body.desktop-view') !== null) ||
      (isMobileOnlyVisible !== null &&
        el.closest('body.mobile-view') !== null) ||
      (isDesktopOnlyVisible === null && isMobileOnlyVisible === null)
    );
  }

  /* istanbul ignore next: there is no mock for script loading */
  protected async _loadExternalResources(
    jsFiles: string[],
    cssFiles: string[] = [],
    force = false
  ): Promise<void> {
    await jsFiles
      .filter(
        (file) =>
          !AbstractMethodComponent.externalResourcesLoaded.has(file) || force
      )
      .reduce(async (promise, url) => {
        await promise;
        return new Promise((resolve, reject) => {
          const script: HTMLScriptElement = document.createElement('script');
          script.setAttribute('src', url);
          script.onload = () => resolve();
          script.onerror = reject;
          document.head.appendChild(script);
          AbstractMethodComponent.externalResourcesLoaded.add(url);
        });
      }, Promise.resolve());

    cssFiles
      .filter(
        (file) =>
          !AbstractMethodComponent.externalResourcesLoaded.has(file) || force
      )
      .forEach((url) => {
        const css: HTMLLinkElement = document.createElement('link');
        css.setAttribute('href', url);
        css.setAttribute('rel', 'stylesheet');
        css.setAttribute('type', 'text/css');
        document.head.appendChild(css);
        AbstractMethodComponent.externalResourcesLoaded.add(url);
      });
  }

  protected _hidePaymentMethodWithError(error) {
    console.log('DS24-PP', this._debugLabel, 'not available. ', error);
    this.notAvailable.emit();
    this._toaster.error(
      $localize`:@@payment_method.message.not_avaiable:Diese Zahlmethode steht momentan nicht zur Verfügung.`
    );
  }

  registerOnChange(fn: any): void {
    if (this._subscriptionRegisterOnChange) {
      this._subscriptionRegisterOnChange.unsubscribe();
    }
    this._subscriptionRegisterOnChange = this._paymentDataChanged.subscribe(fn);
  }

  registerOnTouched(fn: any): void {}

  writeValue(obj: any): void {}

  ngOnDestroy() {
    if (this._subscriptionRegisterOnChange) {
      this._subscriptionRegisterOnChange.unsubscribe();
    }
  }
}
